import { computed, Ref } from 'vue'
import { CustomFieldsSchema } from '@/models/types/custom-fields-schema'
import { Hub } from '@/models/business-unit'
import Task from '@/models/task.js'
import type {
  JobFilterOperation,
  JobFilterProperty,
  JobFilterType,
  JobFilterValue,
} from '@ironsight/types'
import { useFilterableCustomDataProperties } from '@/components/jobs/job-search-page/useFilterableCustomDataProperties'
import { useGetters } from 'vuex-composition-helpers'

export const SUPPORTED_JOB_FILTER_OPERATIONS: JobFilterOperation[] = [
  {
    filterType: 'Text',
    comparison: 'Equals',
    label: 'Is',
  },
  {
    filterType: 'Text',
    comparison: 'Contains',
    label: 'Contains',
  },
  {
    filterType: 'Text',
    comparison: 'StartsWith',
    label: 'Starts with',
  },
  {
    filterType: 'Text',
    comparison: 'IsEmpty',
    label: 'Is empty',
  },
  {
    filterType: 'Integer',
    comparison: 'Equals',
    label: 'Is',
  },
  {
    filterType: 'Integer',
    comparison: 'IsEmpty',
    label: 'Is empty',
  },
  {
    filterType: 'Integer',
    comparison: 'GreaterThan',
    label: 'Greater Than',
    chipLabel: 'is greater than',
  },
  {
    filterType: 'Integer',
    comparison: 'LessThan',
    label: 'Less than',
    chipLabel: 'is less than',
  },

  {
    filterType: 'Decimal',
    comparison: 'GreaterThan',
    label: 'Greater than',
    chipLabel: 'is greater than',
  },
  {
    filterType: 'Decimal',
    comparison: 'IsEmpty',
    label: 'Is empty',
  },
  {
    filterType: 'Decimal',
    comparison: 'LessThan',
    label: 'Less than',
    chipLabel: 'is less than',
  },

  {
    filterType: 'Double',
    comparison: 'Equals',
    label: 'Is',
  },
  {
    filterType: 'Double',
    comparison: 'IsEmpty',
    label: 'Is empty',
  },
  {
    filterType: 'Double',
    comparison: 'GreaterThan',
    label: 'Greater than',
    chipLabel: 'is greater than',
  },
  {
    filterType: 'Double',
    comparison: 'LessThan',
    label: 'Less than',
    chipLabel: 'is less than',
  },

  {
    filterType: 'DateTime',
    comparison: 'GreaterThan',
    label: 'After',
    chipLabel: 'is after',
  },
  {
    filterType: 'DateTime',
    comparison: 'LessThan',
    label: 'Before',
    chipLabel: 'is before',
  },
  {
    filterType: 'DateTime',
    comparison: 'Between',
    label: 'Between',
    chipLabel: 'is between',
  },

  {
    filterType: 'Boolean',
    comparison: 'Equals',
    label: 'Is',
  },

  {
    filterType: 'StringLookup',
    comparison: 'In',
    label: 'Include any of',
    chipLabel: 'is any of',
  },
  {
    filterType: 'StringLookup',
    comparison: 'Equals',
    label: 'Is',
  },
  {
    filterType: 'StringLookup',
    comparison: 'NotIn',
    label: 'Do not include',
    chipLabel: 'does not include',
  },
  {
    filterType: 'StringLookup',
    comparison: 'All',
    label: 'Include all of',
    chipLabel: 'includes all of',
  },

  {
    filterType: 'IntLookup',
    comparison: 'In',
    label: 'Include any of',
    chipLabel: 'is any of',
  },
  {
    filterType: 'IntLookup',
    comparison: 'Equals',
    label: 'Is',
  },
  {
    filterType: 'IntLookup',
    comparison: 'NotIn',
    label: 'Do not include',
    chipLabel: 'does not include',
  },
  {
    filterType: 'IntLookup',
    comparison: 'All',
    label: 'Include all of',
    chipLabel: 'includes all of',
  },
]

const standardFilterableProperties: JobFilterProperty[] = [
  {
    name: 'Status',
    label: 'Job Status',
    icon: 'linear_scale',
    filterType: 'StringLookup',
    supportedComparisons: ['In'],
    visibilityType: 'Hidden',
  },
  {
    name: 'Priority',
    label: 'Priority',
    icon: 'outlined_flag',
    filterType: 'IntLookup',
    supportedComparisons: ['In'],
  },
  {
    name: 'HubId',
    label: 'Hub',
    icon: 'hive',
    filterType: 'StringLookup',
    supportedComparisons: ['In'],
  },
  {
    name: 'OwnerId',
    label: 'Division',
    icon: 'hive',
    filterType: 'StringLookup',
    supportedComparisons: ['In'],
  },
  {
    name: 'SubmitterSid',
    label: 'Creator',
    icon: 'record_voice_over',
    filterType: 'StringLookup',
    supportedComparisons: ['In'],
  },
  {
    name: 'RequesterSid',
    label: 'Requester',
    icon: 'person_pin',
    filterType: 'StringLookup',
    supportedComparisons: ['In'],
  },
  {
    name: 'Followers',
    label: 'Followers',
    icon: 'people_alt',
    filterType: 'StringLookup',
    supportedComparisons: ['In'],
    canBeEmpty: true,
  },
  {
    name: 'ResourceTypeId',
    label: 'Resource Type',
    icon: 'local_shipping',
    filterType: 'IntLookup',
    supportedComparisons: ['In'],
  },
  {
    name: 'TaskId',
    label: 'Activity',
    icon: 'build',
    filterType: 'IntLookup',
    supportedComparisons: ['In'],
  },
  {
    name: 'ServiceProviderId',
    label: 'Service Provider',
    icon: 'villa',
    filterType: 'StringLookup',
    supportedComparisons: ['In'],
    visibilityType: 'Producer',
  },
  {
    name: 'ResourceId',
    label: 'Resource',
    icon: 'navigation',
    filterType: 'IntLookup',
    supportedComparisons: ['In'],
  },
  {
    name: 'FieldWorker',
    label: 'Field Worker',
    icon: 'engineering',
    filterType: 'StringLookup',
    supportedComparisons: ['In'],
    canBeEmpty: true,
  },
  {
    name: 'LocationId',
    label: 'Location',
    icon: 'explore',
    filterType: 'IntLookup',
    supportedComparisons: ['In'],
  },
  {
    name: 'PickupLocationId',
    label: 'Pickup Location',
    icon: 'place',
    filterType: 'IntLookup',
    supportedComparisons: ['In'],
  },
  {
    name: 'ConfirmedPickupLocationId',
    label: 'Confirmed Pickup Location',
    icon: 'place',
    filterType: 'IntLookup',
    supportedComparisons: ['In'],
  },
  {
    name: 'DestinationLocationId',
    label: 'Destination Location',
    icon: 'flag',
    filterType: 'IntLookup',
    supportedComparisons: ['In'],
  },
  {
    name: 'ConfirmedDestinationLocationId',
    label: 'Confirmed Destination Location',
    icon: 'flag',
    filterType: 'IntLookup',
    supportedComparisons: ['In'],
  },
  {
    name: 'InventorySubTypeId',
    label: 'Inventory Requested',
    icon: 'shopping_cart',
    filterType: 'IntLookup',
    supportedComparisons: ['In'],
  },
  {
    name: 'ConfirmedInventorySubTypeId',
    label: 'Confirmed Pickup Inventory',
    icon: 'shopping_cart',
    filterType: 'IntLookup',
    supportedComparisons: ['In'],
  },
  {
    name: 'ConfirmedDropoffInventorySubTypeId',
    label: 'Confirmed Dropoff Inventory',
    icon: 'shopping_cart',
    filterType: 'IntLookup',
    supportedComparisons: ['In'],
  },
  {
    name: 'StartTime',
    label: 'Planned Start Date',
    icon: 'date_range',
    filterType: 'DateTime',
    supportedComparisons: ['Between'],
    visibilityType: 'Hidden',
  },
  {
    name: 'CompletedTime',
    label: 'Completed Date',
    icon: 'event_available',
    filterType: 'DateTime',
    supportedComparisons: ['Between'],
    visibilityType: 'Hidden',
  },
  {
    name: 'CostCenter',
    label: 'Cost Center',
    icon: 'payment',
    filterType: 'StringLookup',
    supportedComparisons: ['In'],
  },
  {
    name: 'Details',
    label: 'Additional Details',
    icon: 'notes',
    filterType: 'Text',
    supportedComparisons: ['Contains'],
  },
  {
    name: 'Tags',
    label: 'Tags',
    icon: 'label',
    filterType: 'IntLookup',
    supportedComparisons: ['In', 'All', 'NotIn'],
    canBeEmpty: true,
  },
  {
    name: 'ProjectId',
    label: 'Project',
    icon: 'view_agenda',
    filterType: 'StringLookup',
    supportedComparisons: ['In'],
    canBeEmpty: true,
  },
  {
    name: 'Batch',
    label: 'Batch',
    filterType: 'StringLookup',
    icon: 'request_quote',
    supportedComparisons: ['In'],
    visibilityType: 'Provider',
    canBeEmpty: true,
  },
  {
    name: 'AssignmentGroupNumber',
    label: 'Assignment Group',
    icon: 'fiber_new',
    filterType: 'Integer',
    supportedComparisons: ['Equals', 'IsEmpty'],
    canBeEmpty: true,
  },
  {
    name: 'IsArchived',
    label: 'Archived',
    icon: 'bookmarks',
    filterType: 'Boolean',
    supportedComparisons: ['Equals'],
    visibilityType: 'Hidden',
  },
  {
    name: 'HasReceipt',
    label: 'Has Receipt',
    icon: 'receipt',
    filterType: 'Boolean',
    supportedComparisons: ['Equals'],
  },
]

const confirmedStopFilterPropertyNames = new Set([
  'ConfirmedPickupLocationId',
  'ConfirmedDestinationLocationId',
  'ConfirmedInventorySubTypeId',
  'ConfirmedDropoffInventorySubTypeId',
])

export function getDefaultValueForType(type: JobFilterType): JobFilterValue {
  switch (type) {
    case 'DateTime': {
      const startOfDay = new Date()
      startOfDay.setHours(0, 0, 0, 0)
      const endOfDay = new Date()
      endOfDay.setHours(23, 59, 59, 999)
      return [startOfDay, endOfDay]
    }
    case 'Boolean':
      return true
    case 'Integer':
    case 'Decimal':
    case 'Double':
    case 'Text':
      return ''
    case 'IntLookup':
    case 'StringLookup':
      return []
    default:
      return ''
  }
}

export function useJobFilterProperties(
  getTasks: Ref<Task[]>,
  getJobSchemas: Ref<CustomFieldsSchema>,
  getAccessibleHubIds: Ref<string[]>,
  isGlobalAdmin: Ref<boolean>,
  getOrgId: Ref<string>,
  getHubs: Ref<Hub[]>,
  getDirectHubIds: Ref<string[]>,
) {
  const { hasStopsEnabledActivities } = useGetters('activities', ['hasStopsEnabledActivities'])
  const shouldShowHubFilter = computed(() => {
    const directHubCount = getDirectHubIds.value.length
    return isGlobalAdmin.value ? directHubCount > 0 : directHubCount > 1
  })

  const userHasAccessToHubOwnedByOrg = computed(() => {
    const accessibleHubIds = getAccessibleHubIds.value
    const organizationId = getOrgId.value
    const allHubs = getHubs.value
    return accessibleHubIds.some((hubId: string) => {
      const hub = allHubs.find((h: Hub) => h.id === hubId)
      return hub && hub.ownerId === organizationId
    })
  })

  const basicFilterableProperties = computed(() => {
    let visibleProperties = userHasAccessToHubOwnedByOrg.value
      ? standardFilterableProperties.filter(
          (prop) =>
            prop.visibilityType === undefined ||
            prop.visibilityType === 'Hidden' ||
            prop.visibilityType === 'Producer',
        )
      : standardFilterableProperties.filter(
          (prop) =>
            prop.visibilityType === undefined ||
            prop.visibilityType === 'Hidden' ||
            prop.visibilityType === 'Provider',
        )

    if (!hasStopsEnabledActivities.value) {
      visibleProperties = visibleProperties.filter(
        (prop) => !confirmedStopFilterPropertyNames.has(prop.name),
      )
    }

    if (!shouldShowHubFilter.value) {
      visibleProperties = visibleProperties.filter((prop) => prop.name !== 'HubId')
    }

    return visibleProperties
  })

  const { simpleCustomDataProperties: customFilterableProperties, customDataFilterMenuTree } =
    useFilterableCustomDataProperties(getTasks, getJobSchemas)

  return {
    basicFilterableProperties,
    customFilterableProperties,
    customDataFilterMenuTree,
  }
}
